/*
RISK-DATE-SELECTOR
Switch date for the risks widget
*/
import TextInput from "@/components/text-input"
import { formatDate, isToday } from "@/utils/format-date.utils"
import riskStore from "../model/risk.store"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import ListButton from "@/components/list-button"
import { faCalendarDays, faCheck, faUndo } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import ContextMenu from "@/components/context-menu"
import { useState, useEffect } from "react"
import { t } from "i18next"

interface RisksDateSelectorProps {
  isCompact?: boolean
}

export default function RisksDateSelector({ isCompact = false }: RisksDateSelectorProps) {
  const dispatch = useAppDispatch()
  const selectedDate = useAppSelector(riskStore.selectors.selectedDate)

  const [isOpen, setIsOpen] = useState(false)
  const [pendingDate, setPendingDate] = useState<Date | null>(selectedDate)

  useEffect(() => {
    setPendingDate(selectedDate)
  }, [selectedDate])

  const today = new Date()
  const todayStr = today.toISOString().split("T")[0]

  function validateDate() {
    if (pendingDate && new Date(pendingDate) > today) {
      toast("Impossible de sélectionner une date future.", { type: "error" })
      return
    }

    const finalDate = isToday(pendingDate) ? today : pendingDate
    dispatch(riskStore.actions.setSelectedDate(finalDate))
    setIsOpen(false)
  }

  function resetDate() {
    const now = new Date()
    setPendingDate(now)
    dispatch(riskStore.actions.setSelectedDate(now))
    setIsOpen(false)
    toast("Retour à la version actuelle.", { autoClose: 2000 })
  }

  return (
    <div className={isCompact ? "flex items-center" : "rel mt-[16px] h-0"}>
      {!isToday(selectedDate) ? (
        isCompact ? (
          <div className="pointer-events-auto text-base">
            <ListButton
              icon={faUndo}
              text="Retour à la version actuelle"
              onClick={resetDate}
            />
          </div>
        ) : (
          <ListButton
            icon={faUndo}
            text="Retour à la version actuelle"
            isPrimary
            isFat
            onClick={resetDate}
          />
        )
      ) : isCompact ? (
        <div className="pointer-events-auto text-base">
          <ListButton
            icon={faCalendarDays}
            tooltipPosition="right"
            text="Rechercher à une date précise"
            onClick={() => setIsOpen(true)}
          />
        </div>
      ) : (
        <ListButton
          icon={faCalendarDays}
          tooltipPosition="right"
          text="Rechercher à une date précise"
          onClick={() => setIsOpen(true)}
        />
      )}

      {isOpen && (
        <ContextMenu onClose={() => setIsOpen(false)} isLarge positionBottom={isCompact}>
          <div className="risks-date-selector" style={{ margin: "0px 30px" }}>
            <p>
              <b>{t("Rechercher à une date précise")}</b>
            </p>
            <p>{t("Retrouvez les éléments archivés de votre document unique à une date antérieure.")}</p>

            <div className="flex gap-2 items-center">
              <TextInput
                type="date"
                value={formatDate(pendingDate || today, false, true)}
                onChange={(e) => setPendingDate(e.value)}
                max={todayStr as any}
              />

              <ListButton icon={faUndo} tooltipPosition="right" text="Aujourd’hui" onClick={resetDate} />

              <ListButton
                icon={faCheck}
                tooltipPosition="right"
                text="Valider"
                isPrimary
                isFat
                onClick={validateDate}
              />
            </div>
          </div>
        </ContextMenu>
      )}
    </div>
  )
}
